import { FugFile, UploadedFile } from '@campfire/file-upload-gallery';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Grid, Theme, Typography } from '@material-ui/core';
import { Sms, Palette } from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { object as YupObject, string as YupString, array as YupArray } from 'yup';
import { AlertCard } from '../../../../../common/cards/alert-card/AlertCard';
// import { EmailConfigSwitch } from './preference-form-sections/EmailConfigSwitch';
import { GreetingAndSignOffFormSection } from './preference-form-sections/GreetingAndSignOffFormSection';
import { LogoFormSection } from './preference-form-sections/LogoFormSection';
import { SmsConfigSwitch } from './preference-form-sections/SmsConfigSwitch';
import { DefaultReplyToFormSection } from './preference-form-sections/DefaultReplyToFormSection';
import { SmsRoleToggles } from './preference-form-sections/SmsRoleToggles';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_ORG_COMM_PREFERENCES } from './admin-console-preferences-model.gql';
import { useAdminConsolePreferencesActions } from './admin-console-preferences-actions';
import { GREETING_OPTIONS, SIGNOFF_OPTIONS } from './AdminConsolePreferencesConsts';
import { FormikErrorFocus } from '../../../../../common/form/FormikErrorFocus';
import { GetOrgCommPreferences } from './__generated__/GetOrgCommPreferences';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

export type PreferenceFormValues = {
  logoAttachments: Array<FugFile>;
  greeting: string;
  customGreeting: string;
  signoff: string;
  customSignOff: string;
  emailNotification: boolean;
  enableSms: boolean;
  defaultReplyTo: string;
  smsEnabledForAdmins: boolean;
  smsEnabledForProgramManagers: boolean;
  smsEnabledForActivityLeaders: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
      display: 'flex',
      justifyContent: 'space-between',
    },
    headingLeft: {
      flex: 1,
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    sectionContainer: {
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
  })
);

const isCustomOption = (type: 'greeting' | 'signoff', value: string): boolean => {
  if (type === 'greeting') return !GREETING_OPTIONS.includes(value);
  return !SIGNOFF_OPTIONS.includes(value);
};

const validationSchema = YupObject().shape({
  logoAttachments: YupArray(),
  greeting: YupString().oneOf(GREETING_OPTIONS.concat('Custom'), 'Choose'),
  customGreeting: YupString().when('greeting', {
    is: 'Custom',
    then: YupString().required('Must enter custom greeting'),
  }),
  signoff: YupString().oneOf(SIGNOFF_OPTIONS.concat('Custom'), 'Choose'),
  customSignoff: YupString().when('signoff', {
    is: 'Custom',
    then: YupString().required('Must enter custom signoff'),
  }),
  defaultReplyTo: YupString().email('Must be a valid email address'),
});

export const AdminConsoleCommunicationPreferences = () => {
  const [uploadedFiles, setUploadedFiles] = useState<Array<UploadedFile>>([]);

  const classes = useStyles();
  const { theme } = useCampfireTheme();

  const {
    runUpdateLogo,
    runUpdateLogoLoading,
    runUpdateSmsPreference,
    runUpdateSmsPreferenceLoading,
    runUpdateGreetingSignoff,
    runUpdateGreetingSignoffLoading,
    runUpdateSmsRolePreferences,
    runUpdateSmsRolePreferencesLoading,
  } = useAdminConsolePreferencesActions();

  const { data: orgCommPreferenceData, loading: orgCommPreferenceLoading } = useCampfireQuery<
    GetOrgCommPreferences,
    undefined
  >(GET_ORG_COMM_PREFERENCES);

  const logo = useMemo(() => orgCommPreferenceData?.tm.tenant.config?.logo, [orgCommPreferenceData]);

  const greeting = useMemo(() => orgCommPreferenceData?.tm.tenant.config?.communication?.greeting, [
    orgCommPreferenceData,
  ]);

  const signoff = useMemo(() => orgCommPreferenceData?.tm.tenant.config?.communication?.signoff, [
    orgCommPreferenceData,
  ]);

  useEffect(() => {
    // hack to make FileUploadGallery work for logo's use case as fileId, and name is required
    setUploadedFiles(
      logo
        ? [
            {
              fileId: logo,
              url: logo,
              name: logo,
            },
          ]
        : []
    );
  }, [logo]);

  const initialPreferences = useMemo(() => {
    return {
      logoAttachments: uploadedFiles,
      greeting: greeting && isCustomOption('greeting', greeting) ? 'Custom' : greeting || '',
      customGreeting: greeting && isCustomOption('greeting', greeting) ? greeting : '',
      signoff: signoff && isCustomOption('signoff', signoff) ? 'Custom' : signoff || '',
      customSignoff: signoff && isCustomOption('signoff', signoff) ? signoff : '',
      enableSms: orgCommPreferenceData?.smsEnabled || false,
      defaultReplyTo: orgCommPreferenceData?.tm.tenant.config?.communication?.replyTo || '',
      smsEnabledForAdmins: orgCommPreferenceData?.tm.tenant.config?.communication?.smsEnabledForAdmins ?? true,
      smsEnabledForProgramManagers:
        orgCommPreferenceData?.tm.tenant.config?.communication?.smsEnabledForProgramManagers ?? false,
      smsEnabledForActivityLeaders:
        orgCommPreferenceData?.tm.tenant.config?.communication?.smsEnabledForActivityLeaders ?? false,
    };
  }, [uploadedFiles, orgCommPreferenceData]);

  const hasInputChanged = (prevValue: any, value: any) => {
    return !isEqual(prevValue, value);
  };

  const hasCommPrefInputChanged = (prevValue: string, customPrevValue: string, value: string, customValue: string) => {
    return prevValue !== value || customPrevValue !== customValue;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialPreferences}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        if (hasInputChanged(initialPreferences.logoAttachments, values.logoAttachments))
          runUpdateLogo({ logo: values.logoAttachments[0] });
        if (hasInputChanged(initialPreferences.enableSms, values.enableSms))
          runUpdateSmsPreference({ enableSms: values.enableSms });
        if (
          hasInputChanged(initialPreferences.smsEnabledForAdmins, values.smsEnabledForAdmins) ||
          hasInputChanged(initialPreferences.smsEnabledForProgramManagers, values.smsEnabledForProgramManagers) ||
          hasInputChanged(initialPreferences.smsEnabledForActivityLeaders, values.smsEnabledForActivityLeaders)
        )
          runUpdateSmsRolePreferences({
            smsEnabledForAdmins: values.smsEnabledForAdmins,
            smsEnabledForProgramManagers: values.smsEnabledForProgramManagers,
            smsEnabledForActivityLeaders: values.smsEnabledForActivityLeaders,
          });
        if (
          hasCommPrefInputChanged(
            initialPreferences.greeting,
            initialPreferences.customGreeting,
            values.greeting,
            values.customGreeting
          ) ||
          hasCommPrefInputChanged(
            initialPreferences.signoff,
            initialPreferences.customSignoff,
            values.signoff,
            values.customSignoff
          ) ||
          hasInputChanged(initialPreferences.defaultReplyTo, values.defaultReplyTo)
        )
          runUpdateGreetingSignoff({
            greeting: values.greeting === 'Custom' ? values.customGreeting : values.greeting,
            signoff: values.signoff === 'Custom' ? values.customSignoff : values.signoff,
            replyTo: values.defaultReplyTo,
          });
      }}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form style={{ height: '100%' }}>
            <Box className={classes.container}>
              <Box className={classes.heading}>
                <Box className={classes.headingLeft}>
                  <Typography variant='h2' className={classes.headingTitle}>
                    Preferences
                  </Typography>
                  <Typography variant={'body2'}>
                    {"Customize your organization's communication settings and branding"}
                  </Typography>
                </Box>
              </Box>

              <Box className={classes.contentContainer}>
                <Box>
                  <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                    <Palette style={{ fontSize: '14px', paddingRight: '5px' }} /> Look and Feel
                  </Typography>
                  <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', marginBottom: '1rem' }}>
                    {"Configure your organisation's branding, messaging preferences, and communication defaults."}
                  </Typography>

                  <AlertCard variant='info' title='About communication preferences'>
                    <Typography className={classes.infoBody}>
                      These settings control how your organisation appears in communications sent to volunteers. You can
                      customise your logo, default greetings and sign-offs, and reply-to addresses. These settings will
                      be applied across all automated emails and messages sent through your Volaby account.
                    </Typography>
                  </AlertCard>
                  <Grid container>
                    <Grid item sm={12} md={6} className={classes.sectionContainer} style={{ paddingRight: '2rem' }}>
                      <LogoFormSection uploadedFiles={uploadedFiles} isLoading={orgCommPreferenceLoading} />
                      <DefaultReplyToFormSection isLoading={orgCommPreferenceLoading} />
                    </Grid>
                    <Grid item sm={12} md={6} className={classes.sectionContainer}>
                      <GreetingAndSignOffFormSection isLoading={orgCommPreferenceLoading} />
                    </Grid>
                  </Grid>
                  {hasInputChanged(initialPreferences, values) ? (
                    <Box display='flex' justifyContent='right' pt={2}>
                      <TabletButton
                        type='submit'
                        variant='contained'
                        color='primary'
                        style={{
                          alignSelf: 'right',
                          backgroundColor: theme.color.secondary.main900,
                          color: '#ffffff',
                          boxSizing: 'border-box',
                          borderRadius: '4px',
                          textTransform: 'none',
                          padding: '7px 12px',
                          minWidth: '40px',
                        }}
                        disabled={
                          runUpdateLogoLoading || runUpdateGreetingSignoffLoading || runUpdateSmsPreferenceLoading
                        }
                      >
                        Save
                      </TabletButton>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Box marginBottom='1.5rem' className={classes.containerPanel}>
                <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
                  <Sms style={{ fontSize: '14px', paddingRight: '5px' }} /> SMS Permissions
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080', marginBottom: '1rem' }}>
                  Control which user roles can send SMS messages to volunteers.
                </Typography>
                <Box>
                  <AlertCard variant='info'>
                    <Typography className={classes.infoBody}>
                      Configure SMS communication settings and control which user roles can send SMS messages to
                      volunteers. Only users with enabled roles will see the SMS option in contact dropdowns and
                      communication interfaces.
                    </Typography>
                  </AlertCard>
                </Box>
                <Box className={classes.sectionContainer}>
                  <SmsConfigSwitch isLoading={orgCommPreferenceLoading} />
                </Box>
                <Box className={classes.sectionContainer}>
                  <SmsRoleToggles
                    isLoading={orgCommPreferenceLoading}
                    disabled={!values.enableSms}
                    smsEnabledForAdmins={values.smsEnabledForAdmins}
                    smsEnabledForProgramManagers={values.smsEnabledForProgramManagers}
                    smsEnabledForActivityLeaders={values.smsEnabledForActivityLeaders}
                    onAdminToggle={() => {
                      setFieldValue('smsEnabledForAdmins', !values.smsEnabledForAdmins);
                    }}
                    onProgramManagerToggle={() => {
                      setFieldValue('smsEnabledForProgramManagers', !values.smsEnabledForProgramManagers);
                    }}
                    onActivityLeaderToggle={() => {
                      setFieldValue('smsEnabledForActivityLeaders', !values.smsEnabledForActivityLeaders);
                    }}
                  />
                </Box>
              </Box>
              {hasInputChanged(initialPreferences, values) ? (
                <Box display='flex' justifyContent='right' pb={4}>
                  <TabletButton
                    type='submit'
                    variant='contained'
                    color='primary'
                    style={{
                      alignSelf: 'right',
                      backgroundColor: theme.color.secondary.main900,
                      color: '#ffffff',
                      boxSizing: 'border-box',
                      borderRadius: '4px',
                      textTransform: 'none',
                      padding: '7px 12px',
                      minWidth: '40px',
                    }}
                    disabled={
                      runUpdateLogoLoading ||
                      runUpdateGreetingSignoffLoading ||
                      runUpdateSmsPreferenceLoading ||
                      runUpdateSmsRolePreferencesLoading
                    }
                  >
                    Save
                  </TabletButton>
                </Box>
              ) : null}
            </Box>
            <FormikErrorFocus />
          </Form>
        );
      }}
    </Formik>
  );
};
